import { useTheme as useVuetifyTheme } from 'vuetify'

export type ThemeMode = 'auto' | 'light' | 'dark'

const THEME_STORAGE_KEY_PREFIX = 'theme-preference'

export const useAppTheme = () => {
  const vuetifyTheme = useVuetifyTheme()
  
  // Get the appropriate storage key based on authentication state
  const getStorageKey = (): string => {
    if (import.meta.client) {
      const userId = sessionStorage.getItem('id')
      if (userId && userId !== '0') {
        // User is authenticated, use user-specific key
        return `${THEME_STORAGE_KEY_PREFIX}-user-${userId}`
      }
    }
    // Not authenticated, use global key for system preference
    return `${THEME_STORAGE_KEY_PREFIX}-system`
  }
  
  // Get the current theme preference from localStorage or default to 'auto'
  const getStoredTheme = (): ThemeMode => {
    if (import.meta.client) {
      const storageKey = getStorageKey()
      const stored = localStorage.getItem(storageKey) as ThemeMode
      return stored && ['auto', 'light', 'dark'].includes(stored) ? stored : 'auto'
    }
    return 'auto'
  }
  
  // Store theme preference in localStorage
  const setStoredTheme = (theme: ThemeMode) => {
    if (import.meta.client) {
      const storageKey = getStorageKey()
      localStorage.setItem(storageKey, theme)
    }
  }
  
  // Detect system color scheme preference
  const getSystemTheme = (): 'light' | 'dark' => {
    if (import.meta.client && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light' // Default to light if system preference unavailable
  }
  
  // Determine the actual theme to apply based on preference
  const resolveTheme = (preference: ThemeMode): 'light' | 'dark' => {
    switch (preference) {
      case 'light':
        return 'light'
      case 'dark':
        return 'dark'
      case 'auto':
      default:
        return getSystemTheme()
    }
  }
  
  // Initialize theme preference based on server-detected theme if available
  const getInitialPreference = (): ThemeMode => {
    if (import.meta.client) {
      // Check if server plugin set a theme that differs from stored preference
      const serverTheme = (window as typeof window & { __NUXT_THEME__?: string }).__NUXT_THEME__
      const storedPreference = getStoredTheme()
      
      if (serverTheme && (serverTheme === 'light' || serverTheme === 'dark')) {
        // If server detected system theme and stored preference is auto, keep it in sync
        if (storedPreference === 'auto') {
          return 'auto'
        }
        // If stored preference doesn't match server theme, user might have changed system preference
        const resolvedStored = resolveTheme(storedPreference)
        if (resolvedStored !== serverTheme && storedPreference === 'auto') {
          return 'auto' // Let it follow system preference
        }
      }
    }
    return getStoredTheme()
  }

  // Reactive theme preference
  const themePreference = ref<ThemeMode>(getInitialPreference())
  
  // Reactive computed actual theme
  const actualTheme = computed(() => resolveTheme(themePreference.value))
  
  // Apply theme to Vuetify and document - Using modern Vuetify 3 API
  const applyTheme = (theme: 'light' | 'dark') => {
    // Ensure the theme exists before applying
    if (vuetifyTheme.themes.value[theme]) {
      // Use the modern Vuetify 3 API to change themes
      vuetifyTheme.change(theme)
    }
    
    // Also update the document attributes for CSS consistency
    if (import.meta.client) {
      document.documentElement.setAttribute('data-theme', theme)
      document.documentElement.classList.remove('v-theme--light', 'v-theme--dark')
      document.documentElement.classList.add(`v-theme--${theme}`)
    }
  }
  
  // Set theme preference and apply it
  const setTheme = (theme: ThemeMode) => {
    themePreference.value = theme
    setStoredTheme(theme)
    // Use nextTick to ensure reactivity
    nextTick(() => {
      applyTheme(actualTheme.value)
    })
  }
  
  // Initialize theme for login screen (system preference only)
  const initializeLoginTheme = () => {
    if (import.meta.client) {
      // For login screen, always use system preference regardless of stored user preference
      const systemTheme = getSystemTheme()
      applyTheme(systemTheme)
    }
  }
  
  // Initialize theme after successful login (load user preference)
  const initializeUserTheme = () => {
    if (import.meta.client) {
      // Refresh theme preference now that user is authenticated
      themePreference.value = getStoredTheme()
      applyTheme(actualTheme.value)
    }
  }
  
  // Revert to system theme on logout
  const revertToSystemTheme = () => {
    if (import.meta.client) {
      // Store system default as the theme preference for non-authenticated state
      const systemTheme = getSystemTheme()
      const systemModeTheme: ThemeMode = 'auto' // Always use auto for system preference
      
      // Clear any user-specific theme preferences
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`${THEME_STORAGE_KEY_PREFIX}-user-`)) {
          localStorage.removeItem(key)
        }
      })
      
      // Set system theme preference and apply it
      themePreference.value = systemModeTheme
      localStorage.setItem(`${THEME_STORAGE_KEY_PREFIX}-system`, systemModeTheme)
      applyTheme(systemTheme)
    }
  }
  
  // Get initial theme from SSR inline script
  const getInitialTheme = (): 'light' | 'dark' => {
    if (import.meta.client) {
      // Check if the inline script set a theme
      const ssrTheme = (window as typeof window & { __NUXT_THEME__?: string }).__NUXT_THEME__
      if (ssrTheme === 'light' || ssrTheme === 'dark') {
        return ssrTheme
      }
      
      // Fallback to reading from document attribute
      const dataTheme = document.documentElement.getAttribute('data-theme')
      if (dataTheme === 'light' || dataTheme === 'dark') {
        return dataTheme
      }
    }
    
    // Final fallback
    return getSystemTheme()
  }

  // Initialize theme on mount
  onMounted(() => {
    // Get the initial theme that was applied by the SSR script
    const initialTheme = getInitialTheme()
    
    // Sync Vuetify with the initial theme without changing document attributes
    // (since they're already set by the inline script)
    if (vuetifyTheme.themes.value[initialTheme]) {
      vuetifyTheme.change(initialTheme)
    }
    
    // Ensure reactive state matches the actually applied theme
    const currentPreference = getStoredTheme()
    if (themePreference.value !== currentPreference) {
      themePreference.value = currentPreference
    }
    
    // Listen for system theme changes when preference is 'auto'
    if (import.meta.client && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleSystemThemeChange = () => {
        if (themePreference.value === 'auto') {
          applyTheme(actualTheme.value)
        }
      }
      
      mediaQuery.addEventListener('change', handleSystemThemeChange)
      
      // Cleanup listener
      onUnmounted(() => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange)
      })
    }
  })
  
  // Watch for changes in actual theme and apply them
  watch(actualTheme, (newTheme) => {
    applyTheme(newTheme)
  })
  
  // Note: Authentication state watching is now handled externally to avoid circular dependencies
  
  return {
    themePreference: readonly(themePreference),
    actualTheme: readonly(actualTheme),
    setTheme,
    getSystemTheme,
    initializeLoginTheme,
    initializeUserTheme,
    revertToSystemTheme,
  }
}